import type { SupabaseClient } from '@supabase/supabase-js';
import { renderOrdersTab } from './tabs/orders.js';
import { renderProductsTab } from './tabs/products.js';
import { renderListToGambioTab } from './tabs/list-to-gambio.js';
import { renderLogTab } from './tabs/log.js';
import { renderHelpTab } from './help.js';

export interface DashboardContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  handshake: { appToken: string; appIdentifier?: string; offlineToken?: string };
  supabaseUrl: string;
  navigate?: (tabId: string, params?: unknown) => void | Promise<void>;
}

interface Tab {
  id: string;
  label: string;
  icon: string; // Font-Awesome-Klasse, z.B. 'fa-receipt'
  render: (host: HTMLElement, ctx: DashboardContext, params?: unknown) => void | Promise<void>;
}

/* Gambio-Tabs (Frontend-Modell A). Gambio-Backend hat aktuell nur die Order-
 * Pull-Richtung + Install/Credentials - daher Bestellungen + Einstellungen.
 * Listing-/Lister-/Optional-Fields-Tabs (Kaufland) kommen, sobald der Gambio-
 * Produktupload gebaut ist. */
const TABS: Tab[] = [
  { id: 'orders',   label: 'Bestellungen',  icon: 'fa-receipt', render: renderOrdersTab },
  { id: 'products', label: 'Produkte',      icon: 'fa-box',     render: renderProductsTab },
  { id: 'list-to-gambio', label: 'Auf Gambio anlegen', icon: 'fa-cloud-arrow-up', render: renderListToGambioTab },
  { id: 'log',      label: 'Log',           icon: 'fa-list',    render: renderLogTab },
  { id: 'help',     label: 'Anleitung',     icon: 'fa-circle-question', render: renderHelpTab },
];

function deriveInitials(source: string): string {
  // Initialen-Heuristik fuer den Avatar:
  //   - "GO eCommerce" → "GE"           (erste Buchstaben der ersten 2 Worte)
  //   - "Werkzeugkasten24 GmbH" → "WG"  (zwei Worte, erste Buchstaben)
  //   - "Werkzeugkasten24" → "W24"      (Wort+Zahlsuffix → Buchstabe+Zahl)
  //   - "Acme" → "AC"                   (erste 2 Buchstaben)
  //   - "" → ''                         (Avatar zeigt nichts an)
  const trimmed = source.trim();
  if (!trimmed) return '';
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  }
  // Single-Word: Buchstabe+Zahl (z.B. "Werkzeugkasten24" → "W24")
  const m = trimmed.match(/^([A-Za-zÄ-ſ])(?:[a-zÄ-ſ]*?)(\d{1,3})\b/);
  if (m) return (m[1]! + m[2]!).toUpperCase();
  // Sonst: erste 2 Buchstaben (kein Zahl-Suffix)
  const onlyLetters = trimmed.replace(/[^A-Za-zÄ-ſ]/g, '');
  return (onlyLetters.slice(0, 2) || trimmed.slice(0, 2)).toUpperCase();
}

export async function renderDashboard(
  ctx: DashboardContext,
) {
  ctx.contentEl.hidden = false;

  // Tenant-Name asynchron nachladen - initial zeigen wir einen Platzhalter,
  // sobald der Name kommt, wird kl-brand-sub + Avatar-Initials updated. Avatar
  // bekommt keinen UUID-Hex-Suffix mehr - bis der name da ist, nur ".." als
  // dezenter Loading-State.
  const initialsFallback = '··';

  const headerHtml = `
      <header class="kl-header">
        <div class="kl-brand">
          <img class="kl-brand-logo" src="/logo-black.png" alt="go!e Commerce" width="835" height="338" />
          <div class="kl-brand-text">
            <div class="kl-brand-line"><span class="kl-brand-slash">Gambio</span></div>
            <div class="kl-brand-sub" id="kl-brand-account">Lade Account …</div>
          </div>
        </div>
        <div class="kl-header-right">
          <span class="kl-connected"><span class="v-icon fa-light fa-circle-check"></span> verbunden</span>
          <div class="kl-user">
            <div class="kl-user-avatar" id="kl-user-avatar">${initialsFallback}</div>
          </div>
        </div>
      </header>`;

  ctx.contentEl.innerHTML = `
    <div class="kl-app">
      ${headerHtml}
      <nav class="kl-tabs" id="kl-tabs" role="tablist"></nav>
      <main class="kl-tab-panel" id="kl-content"></main>
    </div>
  `;

  // account.tenants.name laden und in die Brand-Sub eintragen. RLS sorgt
  // dafuer, dass der eingeloggte User nur seinen eigenen Tenant sieht.
  void (async () => {
    const { data, error } = await ctx.supabase
      .schema('account')
      .from('tenants')
      .select('name')
      .eq('id', ctx.session.tenantId)
      .maybeSingle();
    const subEl = document.getElementById('kl-brand-account');
    const avatarEl = document.getElementById('kl-user-avatar');
    if (error || !data?.name) {
      // Kein Name verfuegbar: Brand-Sub komplett verstecken (keine UUID-
      // Anzeige), Avatar bleibt mit '..' Loading-State.
      if (subEl) {
        subEl.textContent = '';
        subEl.style.display = 'none';
      }
      return;
    }
    if (subEl) subEl.textContent = data.name;
    if (avatarEl) avatarEl.textContent = deriveInitials(data.name);
  })();

  const tabsEl = ctx.contentEl.querySelector<HTMLElement>('#kl-tabs')!;
  const body = ctx.contentEl.querySelector<HTMLElement>('#kl-content')!;

  // Aktiven Tab über Reloads (F5) hinweg merken - sonst landet man nach jedem
  // Reload wieder auf dem ersten Tab. sessionStorage (pro Browser-Tab, nicht persistent).
  const ACTIVE_TAB_KEY = 'gambio:dashboard:activeTab';
  const savedTab = (() => { try { return sessionStorage.getItem(ACTIVE_TAB_KEY); } catch { return null; } })();
  let active = (savedTab && TABS.some((t) => t.id === savedTab)) ? savedTab : TABS[0]!.id;

  function renderTabBar() {
    const tabBtns = TABS.map(
      (t) => `
        <button type="button"
                class="kl-tab ${active === t.id ? 'is-active' : ''}"
                data-tab="${t.id}"
                role="tab"
                aria-selected="${active === t.id}">
          <span class="v-icon fa-light ${t.icon}"></span>
          ${t.label}
        </button>
      `,
    ).join('');
    // Refresh-Button rechts: lädt den AKTIVEN Tab neu (Daten frisch ziehen), ohne F5
    // und ohne Tab-/Filter-Verlust. Tabs persistieren ihre Suchfilter selbst (sessionStorage).
    const refreshBtn = `
      <button type="button" class="kl-tab kl-tab-refresh" id="kl-tab-refresh" style="margin-left:auto;"
              title="Aktuellen Tab neu laden (ohne Seiten-Reload)">
        <span class="v-icon fa-light fa-rotate-right" id="kl-tab-refresh-icon"></span>
        Aktualisieren
      </button>`;
    tabsEl.innerHTML = tabBtns + refreshBtn;
    for (const btn of tabsEl.querySelectorAll<HTMLButtonElement>('.kl-tab[data-tab]')) {
      btn.addEventListener('click', () => activate(btn.dataset.tab!));
    }
    const rb = tabsEl.querySelector<HTMLButtonElement>('#kl-tab-refresh');
    rb?.addEventListener('click', () => {
      const icon = rb.querySelector<HTMLElement>('#kl-tab-refresh-icon');
      icon?.classList.add('fa-spin');
      void activate(active).finally(() => icon?.classList.remove('fa-spin'));
    });
  }

  async function activate(tabId: string, params?: unknown) {
    const tab = TABS.find((t) => t.id === tabId) ?? TABS[0]!;
    active = tab.id;
    try { sessionStorage.setItem(ACTIVE_TAB_KEY, active); } catch { /* ignore */ }
    renderTabBar();
    body.innerHTML = '';
    await tab.render(body, ctx, params);
  }
  ctx.navigate = (tabId, params) => activate(tabId, params);

  renderTabBar();
  await activate(active);
}
